import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectGuruWidgetOpen } from 'ducks/guruWidget/selectors'

/**
 * Shared ordering for the floating controls that stack above the bottom bar on
 * mobile. Fixed rows leave holes whenever one of the controls is not on screen,
 * so instead every visible control registers itself here and gets the next free
 * slot, counted from the bottom.
 *
 * Lower priority sits closer to the thumb.
 *
 * Stacking rule: every control in this stack sits at FLOATING_Z_CEILING, below
 * the lowest modal/drawer backdrop in the app (999). That way any overlay
 * covers the stack without having to register itself.
 */
export const FLOATING_Z_CEILING = 900
export const FLOATING_PRIORITY = {
  launcher: 0,
  info: 10,
  menu: 20,
  reminder: 30,
}

const SLOT_STEP = 58 // 46px button + 12px gap
const QUERY = '(max-width: 768px)'

const registry = new Map()
const overlays = new Set()
const listeners = new Set()

/*
 * Mobile browsers show and hide their toolbar as you scroll. `position: fixed`
 * is anchored to the layout viewport, so when the toolbar comes back on scroll
 * up it covers whatever sits at the bottom — the floating buttons disappear.
 * Publish the covered height (toolbar, or the on-screen keyboard) so the stack
 * can lift itself out of the way.
 */
let viewportWatcherStarted = false

const publishViewportBottom = () => {
  const vv = window.visualViewport
  if (!vv) return
  // While pinch-zoomed the visual viewport is smaller for a different reason;
  // lifting the stack then would just make it jump around.
  const covered = vv.scale > 1.01
    ? 0
    : Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop))
  document.documentElement.style.setProperty('--bp-viewport-bottom', `${covered}px`)
}

const startViewportWatcher = () => {
  if (viewportWatcherStarted) return
  if (typeof window === 'undefined' || !window.visualViewport) return
  viewportWatcherStarted = true
  publishViewportBottom()
  window.visualViewport.addEventListener('resize', publishViewportBottom)
  window.visualViewport.addEventListener('scroll', publishViewportBottom)
}

const publishHeight = () => {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (registry.size === 0) root.style.removeProperty('--bp-stack-height')
  else root.style.setProperty('--bp-stack-height', `${registry.size * SLOT_STEP}px`)
}

const notify = () => {
  publishHeight()
  listeners.forEach(listener => listener())
}

const slotIndex = id => {
  const ordered = [...registry.entries()].sort(
    (a, b) => a[1] - b[1] || String(a[0]).localeCompare(String(b[0])),
  )
  const index = ordered.findIndex(([key]) => key === id)
  return index < 0 ? 0 : index
}

const matchNarrow = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(QUERY).matches
    : false

export const useIsNarrow = () => {
  const [narrow, setNarrow] = useState(matchNarrow)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
    const mq = window.matchMedia(QUERY)
    const onChange = event => setNarrow(event.matches)
    setNarrow(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  return narrow
}

/**
 * Declares a full-screen overlay (the mobile nav sheet, a drawer) so the
 * floating controls step aside while it is open — otherwise they float on top
 * of the menu and swallow taps meant for it.
 */
export const useFloatingOverlay = (id, active) => {
  useEffect(() => {
    if (!active) return undefined
    overlays.add(id)
    notify()
    return () => {
      overlays.delete(id)
      notify()
    }
  }, [id, active])
}

/**
 * True while the whole floating stack should stay out of the way.
 */
export const useFloatingHidden = () => {
  const guruOpen = useSelector(selectGuruWidgetOpen)
  const narrow = useIsNarrow()
  const [covered, setCovered] = useState(() => overlays.size > 0)

  useEffect(() => {
    const update = () => setCovered(overlays.size > 0)
    listeners.add(update)
    update()
    return () => {
      listeners.delete(update)
    }
  }, [])

  return narrow && (guruOpen || covered)
}

/**
 * @param {string} id      unique per control
 * @param {number} priority see FLOATING_PRIORITY
 * @param {boolean} active  whether the control is currently on screen
 * @returns {{ hidden: boolean, style: object }}
 *   `hidden` is true while the Guru sheet covers the screen — render nothing.
 *   `style` carries the slot offset as a custom property, so only the mobile
 *   media query that reads --bp-slot is affected and desktop stays untouched.
 */
export const useFloatingSlot = (id, priority, active = true) => {
  const narrow = useIsNarrow()
  const hidden = useFloatingHidden()
  const [index, setIndex] = useState(0)

  const registered = active && narrow && !hidden

  useEffect(() => {
    startViewportWatcher()
  }, [])

  useEffect(() => {
    if (!registered) return undefined
    registry.set(id, priority)
    notify()
    return () => {
      registry.delete(id)
      notify()
    }
  }, [id, priority, registered])

  useEffect(() => {
    const update = () => setIndex(slotIndex(id))
    listeners.add(update)
    update()
    return () => {
      listeners.delete(update)
    }
  }, [id, registered])

  return {
    hidden,
    style: { '--bp-slot': `calc(var(--bp-float-1) + ${index * SLOT_STEP}px)` },
  }
}

export default useFloatingSlot
